import React, { useState, useEffect, useCallback, useMemo } from "react";
import { connect, useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import _isLoading from "hoc/isLoading";
import {
  DeleteMembers,
  getOfficesDetail,
  startLoading,
  stopLoading,
} from "actions/index";
import Toast from "components/Toast";
import SubstituteSelectorFactory from "components/SubstituteSelector";
import { getGlobalStaffDetail } from "repositories/staff-repository";
import { getLeaveDetail } from "repositories/leave-repository";
import { getPerformanceReviews } from "repositories/performance-repository";
import * as moment from "moment";
import Page from "components/Page";
import TabsView from "./TabsView";
import StaffProfileSection from "./StaffProfileSection";
import SectionHeader from "./SectionHeader";
import PersonalDetailsSection from "./PersonalDetailsSection";
import RoleDetailsSection from "./RoleDetailsSection";
import StaffActionDropdown from "./StaffActionDropdown";
import { useStaffActions } from "./useStaffActions";
import CustomDropdown from "components/Dropdown";
import detailStyles from "./staffDetails.module.scss";
import tick_icon from "assets/images/icon_tick.svg";
import Loader from "components/Loader";
import { convertStaffData } from "./staffdetail.functions";
import styles from "../staff.module.scss";
import constants from "../../../constants";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
// staffMemberId = user id
// staffId = user office id
// inviteId = user invitee office id
// staff {type}
// 1=> activated
// 2=> deactivated
// 3=> invitation pending
// 4=> invitation declined

const REVIEW_TYPES = [
  "General",
  "ProbationPeriod",
  "MonthlyReview",
  "QuarterlyReview",
  "YearlyReview",
  "Others",
];

const StaffDetail = ({
  statusMessage,
  isStaffDelete,
  isStaffDeleteError,
  t,
  officeDetail,
  profile,
  getOfficesDetail,
}) => {
  const history = useHistory();
  const location = useLocation();
  const locationState = location.state;
  const globalStaffPage = useMemo(
    () => location.pathname.includes("global-staff"),
    [location.pathname],
  );
  const isLoading = useSelector((state) => state.staff.isLoading);
  const [isToastView, setIsToastView] = useState(false);
  const { selectedId, getIdToCheckOwnerChange } = useAccountOwnerCommon();

  // const [deleteModal, setDeleteModal] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(true);
  const [reviewFormList, setReviewFormList] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [globalStaffDetail, setGlobalStaffDetail] = useState({});
  const [officeIdSelectedView, setofficeIdSelectedView] = useState(0);
  const staffActions = useStaffActions(globalStaffDetail);

  const fetchPerformanceReviews = useCallback(
    async (officeId, userId, performanceReviewId) => {
      const apiRes = await getPerformanceReviews(officeId, userId);
      if (apiRes.statusCode !== 200 || !apiRes.data || !apiRes.data.length)
        return;

      if (performanceReviewId) {
        apiRes.data.forEach((elm) => {
          if (+elm.id === +performanceReviewId) {
            const reviewFormList =
              moment(elm.createdAt).format("MMMM DD, YYYY") +
              " - " +
              REVIEW_TYPES[elm.type];
            setReviewFormList(reviewFormList);
          }
        });
      }
    },
    [locationState],
  );

  const fetchLeaveDetail = useCallback(async (staffId, invitationId) => {
    const apiRes = staffId
      ? await getLeaveDetail(staffId)
      : await getLeaveDetail(null, invitationId);

    if (apiRes.statusCode === 200) {
      setLeaves(apiRes.data);
    }
  }, []);

  // Redirect if no location state
  useEffect(() => {
    if (!locationState) {
      history.push("/Offices");
    }
  }, [locationState, history]);

  const ownerId = locationState?.ownerId || officeDetail?.ownerId;
  useEffect(() => {
    if (globalStaffPage && getIdToCheckOwnerChange()) {
      history.push(constants.routes.globalStaffList);
    }
  }, [selectedId]);
  useEffect(() => {
    if (!locationState) return;

    const initializeData = async () => {
      const staffDetail = locationState.detail;
      if (
        globalStaffPage ||
        (!globalStaffPage && locationState.OfficeId && ownerId)
      ) {
        try {
          setGlobalLoading(true);
          const res = await getGlobalStaffDetail({
            ownerId: ownerId,
            userId: locationState?.detail?.userId || staffDetail.staffMemberId,
            isGlobal: globalStaffPage,
            ...(locationState.OfficeId && { officeId: locationState.OfficeId }),
          });
          const data = res.data;
          if (data) {
            const allOffices = [];
            data.invitationInvitee.forEach((res) => {
              allOffices.push({
                id: res.officeId,
                name: res.office.name,
                inviteId: res.id,
                otherDetails: res,
              });
            });
            data.userOffice.forEach((res) => {
              allOffices.push({
                id: res.officeId,
                name: res.office.name,
                staffId: res.id,
                otherDetails: res,
              });
            });
            if (data.invitationInvitee.length) {
              setofficeIdSelectedView(data.invitationInvitee[0].officeId);
            } else if (data.userOffice.length) {
              setofficeIdSelectedView(data.userOffice[0].officeId);
            }
            data.allOffices = allOffices;
            setGlobalStaffDetail(data);
          }
        } catch (error) {
        } finally {
          setGlobalLoading(false);
        }
        return;
      }
      // // Fetch office details
      getOfficesDetail({ Id: locationState.OfficeId });
    };

    initializeData();
  }, [
    locationState,
    getOfficesDetail?.id,
    officeDetail?.id,
    globalStaffPage,
    ownerId,
  ]);

  // Handle staff delete status changes
  useEffect(() => {
    if (isStaffDelete !== undefined || isStaffDeleteError !== undefined) {
      setIsToastView(true);
      const timer = setTimeout(() => {
        setIsToastView(false);
        if (isStaffDelete && locationState) {
          staffActions.goBackToStaffList();
        }
      }, 2500);
      return () => clearTimeout(timer);
    }
  }, [isStaffDelete, isStaffDeleteError, locationState]);

  const {
    staffRoleId,
    isAdmin,
    designation,
    employmentDate,
    type,
    colourCode,
    departmentName,
    reportToName,
    staffId,
    staffMemberId,
    isLoggedInUserSeeHourlyRate,
    schedules,
    ...restSpecifiValues
  } = useMemo(() => {
    if (globalStaffDetail?.allOffices) {
      return convertStaffData({
        fetchLeaveDetail,
        fetchPerformanceReviews,
        allOffices: globalStaffDetail.allOffices,
        officeIdSelectedView,
        staffMemberId: globalStaffDetail?.id,
        allData: globalStaffDetail,
      });
    }
    return {};
  }, [globalStaffDetail, officeIdSelectedView]);
  const staffAllDetails = {
    staffRoleId,
    isAdmin,
    designation,
    employmentDate,
    type,
    colourCode,
    departmentName,
    reportToName,
    staffId,
    staffMemberId,
    isLoggedInUserSeeHourlyRate,
    schedules,
    ...restSpecifiValues,
  };
  // const handleStaffAction = useCallback(async () => {
  //   const eventWas = staffActions.staffActionEvent;
  //   staffActions.setStaffActionEvent(null);
  //   startLoading();

  //   const userId = staffMemberId;
  //   const officeId = officeIdSelectedView;

  //   if (!userId || !officeId) {
  //     stopLoading();
  //     return;
  //   }

  //   let res;
  //   if (eventWas === constants.staffActionEvents.activation) {
  //     res = await staffRepository.activateStaff(userId, officeId);
  //   } else if (eventWas === constants.staffActionEvents.deactivation) {
  //     res = await staffRepository.deactivateStaff(userId, officeId, null);
  //   } else if (eventWas === constants.staffActionEvents.removal) {
  //     res = await staffRepository.removeStaff(userId, officeId, null);
  //   }

  //   stopLoading();
  //   if (res?.statusCode === 200) {
  //     staffActions.goBackToStaffList();
  //   }
  // }, [staffActions, startLoading, stopLoading, staffMemberId, officeDetail]);

  if (!locationState) {
    return null;
  }
  const {
    profilePic,
    firstName,
    lastName,
    emailId,
    contactNumber,
    address,
    socialSecurityNumber,
    state,
    postalCode,
    country,
    city,
    streetAddress,
  } = globalStaffDetail;
  const officeId = officeIdSelectedView || "";

  // Render substitute selector if needed
  if (staffActions.substituteSelectionEvent !== null) {
    if (!staffMemberId) return null;
    return (
      <SubstituteSelectorFactory
        staff={{
          id: staffId,
          userId: staffMemberId,
          profilePicURL: profilePic,
          firstName,
          lastName,
          officeId,
          globalStaffDetail,
          selectedId: ownerId,
        }}
        backRedirection={!!location.search}
        event={staffActions.substituteSelectionEvent}
        onAction={staffActions.substituteSelectorAction}
        isActionExecuting={staffActions.isSubstituteSelectorActionExecuting}
        errorMessage={staffActions.substituteSelectorErrorMessage}
        onClearErrorMessage={staffActions.clearSubstituteSelectorErrorMessage}
        onCancel={staffActions.exitSubstituteSelectionJourney}
      />
    );
  }

  const shouldShowDropdown =
    (globalStaffPage
      ? true
      : locationState && officeDetail && officeDetail.isActive) &&
    profile &&
    profile.profileSetupStep !== "packageExpired" &&
    profile.profileSetupStep !== "subscriptionTerminated";

  const isHourlyRateCanView = profile?.isAdmin
    ? isLoggedInUserSeeHourlyRate
    : true;
  const officeFirstData =
    globalStaffPage && globalStaffDetail?.allOffices?.length === 1
      ? globalStaffDetail.allOffices[0].otherDetails
      : null;
  const allTypeSame =
    globalStaffPage && globalStaffDetail?.allOffices?.length
      ? globalStaffDetail?.allOffices.reduce((prev, curr, i) => {
          if (!i) {
            prev = curr.otherDetails.type;
          }
          if (i && prev !== curr.otherDetails.type) {
            return 0;
          }
          return prev;
        }, 0)
      : 0;
  const modiFiedType = officeFirstData
    ? officeFirstData.type
    : globalStaffPage
      ? allTypeSame
      : type;
  const addressView = [
    address || streetAddress,
    city?.name || "",
    state?.name || "",
    country?.name || "",
    postalCode ? `(${postalCode})` : "",
  ]
    .filter((res) => !!res)
    .toString();

  return (
    <div className="staff-detail-block p-0">
      {isToastView && statusMessage && (
        <Toast
          message={statusMessage}
          handleClose={() => setIsToastView(false)}
          errorToast={!!isStaffDeleteError}
        />
      )}
      {(globalLoading || isLoading) && <Loader />}
      <Page onBack={staffActions.goBackToStaffList}>
        <div className="container-smd">
          <div className="row no-gutters align-items-center page_title_margin_top">
            <div className="col-md-7">
              <h2 className="page-title">{t("accountOwner.staffDetails")}</h2>
              {globalStaffPage ? null : (
                <p className="sub-title">{officeDetail?.name || null}</p>
              )}
            </div>
          </div>

          <div className="staff-detail-content">
            <div className="row no-gutters">
              <div className="col-md-3">
                <StaffProfileSection
                  profilePic={profilePic}
                  firstName={firstName}
                  lastName={lastName}
                  colourCode={colourCode}
                />
              </div>

              <div className="col-md-9">
                <div className="staff-job-detail">
                  <SectionHeader className="mb-3">
                    Personal Details
                  </SectionHeader>
                  <PersonalDetailsSection
                    emailId={emailId}
                    contactNumber={contactNumber}
                    socialSecurityNumber={socialSecurityNumber}
                    address={addressView}
                    t={t}
                  />

                  <SectionHeader
                    className={`d-flex justify-content-between ${styles["staff-role-margin"]}`}
                  >
                    {globalStaffPage ? "Office" : "Role"}-Specific Details
                    {globalStaffPage &&
                      globalStaffDetail?.allOffices?.length > 0 && (
                        <div
                          className={
                            "custom-dropdown-only " +
                            detailStyles["office-dropdown"]
                          }
                        >
                          <CustomDropdown
                            options={globalStaffDetail?.allOffices}
                            defaultValue={restSpecifiValues?.office?.name}
                            renderAnyThing={(id) =>
                              id === officeIdSelectedView && (
                                <img
                                  src={tick_icon}
                                  alt="tick"
                                  className="mr-2"
                                />
                              )
                            }
                            selectOption={(id) => {
                              setofficeIdSelectedView(id);
                              // if (onChange) {
                              //   onChange(parseInt(id));
                              // }
                            }}
                          />
                        </div>
                      )}
                  </SectionHeader>
                  <RoleDetailsSection
                    designation={designation}
                    staffRoleId={staffRoleId}
                    departmentName={departmentName}
                    reportToName={reportToName}
                    schedules={schedules}
                    employmentDate={employmentDate}
                    isAdmin={isAdmin}
                    globalStaffPage={globalStaffPage}
                    staffDetails={staffAllDetails}
                    t={t}
                  >
                    <TabsView
                      isHourlyRateCanView={isHourlyRateCanView}
                      officeId={officeId}
                      reviewFormList={reviewFormList}
                      staffDetails={staffAllDetails}
                      staffLeaves={leaves}
                    />
                  </RoleDetailsSection>
                </div>
              </div>
            </div>

            {shouldShowDropdown && (
              <StaffActionDropdown
                type={modiFiedType}
                officeId={officeId}
                data={{
                  profilePic,
                  firstName,
                  lastName,
                  emailId,
                  contactNumber,
                  address,
                  socialSecurityNumber,
                  staffRoleId,
                  isAdmin,
                  designation,
                  employmentDate,
                  type,
                  colourCode,
                  departmentName,
                  reportToName,
                  staffId,
                  staffMemberId,
                  officeList: globalStaffPage
                    ? globalStaffDetail?.allOffices?.map((res) => {
                        res.otherDetails.officeName = res.name;
                        return res.otherDetails;
                      }) || []
                    : undefined,
                  ...restSpecifiValues,
                  canAdminSeeHourlyRate: isHourlyRateCanView,
                }}
                globalStaffPage={globalStaffPage}
                officeDetail={officeDetail}
                selectedId={ownerId}
                // onRemoveStaffClick={staffActions.onRemoveStaffDropdownItemClick}
                // onDeactivateStaffClick={
                //   staffActions.onDeactivateStaffDropdownItemClick
                // }
                // onActivateStaffClick={
                //   staffActions.onActivateStaffDropdownItemClick
                // }
                // onCancelInvitationClick={() => setDeleteModal(true)}
                t={t}
                staffActionsSubstitueActionSetter={
                  staffActions.setSubstituteSelectionEvent
                }
              />
            )}

            {/* <DeleteConfirmationModal
              isOpen={deleteModal}
              onClose={() => setDeleteModal(false)}
              onConfirm={handleUserDelete}
              t={t}
            /> */}
          </div>
        </div>
      </Page>

      {/* <StaffActionModal
        isOpen={staffActions.staffActionEvent !== null}
        event={staffActions.staffActionEvent}
        onAction={handleStaffAction}
        onClose={() => staffActions.setStaffActionEvent(null)}
      /> */}
    </div>
  );
};

const mapStateToProps = ({
  userProfile: { profile },
  staff: { statusMessage, isLoading, isStaffDelete, isStaffDeleteError },
  offices: { officeDetail },
  errors: { isError },
}) => ({
  statusMessage,
  isLoading,
  isError,
  isStaffDelete,
  isStaffDeleteError,
  profile,
  officeDetail,
});

export default connect(mapStateToProps, {
  DeleteMembers,
  getOfficesDetail,
  startLoading,
  stopLoading,
})(_isLoading(withTranslation()(StaffDetail)));
